import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import StatsCard from '../../components/Admin/StatsCard';
import SalesChart from '../../components/Admin/SalesChart';
import RevenueChart from '../../components/Admin/RevenueChart';
import OrderDetailsModal from '../../components/Admin/OrderDetailsModal';
import UsersListModal from '../../components/Admin/UsersListModal';
import { libelleStatut, classeStatut, demandeUneAction } from '../../lib/statutsCommande';

const DashboardHome = () => {
    const [analytics, setAnalytics] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [showUsersModal, setShowUsersModal] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const ORDERS_PER_PAGE = 5;

    useEffect(() => {
        fetchAnalytics();
    }, []);

    const fetchAnalytics = async () => {
        try {
            const response = await api.get('/admin/analytics');
            setAnalytics(response.data);
        } catch (err) {
            console.error('Error fetching analytics:', err);
            setError('Impossible de charger les données analytiques.');
        } finally {
            setLoading(false);
        }
    };

    if (loading) return <div className="dashboard-loading">Chargement du tableau de bord...</div>;
    if (error) return <div className="dashboard-error">{error}</div>;
    if (!analytics) return null;

    return (
        <div className="min-h-screen bg-gray-50 p-8">
            <h1 className="mb-8 text-3xl font-bold text-gray-900">Tableau de Bord</h1>

            {/* Stats Grid — ligne 1 : les grands totaux */}
            <div className="mb-6 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                <StatsCard
                    title="Chiffre d'Affaires Total"
                    value={`${analytics.totalRevenue.toFixed(2)}€`}
                    icon="💰"
                    color="green"
                    trend={analytics.revenueTrend ?? undefined}
                />
                <StatsCard
                    title="Commandes Totales"
                    value={analytics.totalOrders}
                    icon="📦"
                    color="blue"
                />
                <StatsCard
                    title="Utilisateurs Inscrits"
                    value={analytics.totalUsers}
                    icon="👥"
                    color="purple"
                    onClick={() => setShowUsersModal(true)}
                />
            </div>

            {/* Stats Grid — ligne 2 : les indicateurs qui font agir */}
            <div className="mb-8 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
                <StatsCard
                    title="CA ce mois-ci"
                    value={`${(analytics.revenueThisMonth ?? 0).toFixed(2)}€`}
                    icon="📈"
                    color="green"
                />
                <StatsCard
                    title="Panier Moyen"
                    value={`${(analytics.avgOrderValue ?? 0).toFixed(2)}€`}
                    icon="🧾"
                    color="blue"
                />
                <StatsCard
                    title="À Encaisser"
                    value={`${(analytics.pendingRevenue ?? 0).toFixed(2)}€`}
                    icon="⏳"
                    color="orange"
                    className={analytics.pendingCount > 0 ? 'ring-1 ring-orange-200' : ''}
                />
                <StatsCard
                    title="Nouveaux Clients (mois)"
                    value={analytics.newUsersThisMonth ?? 0}
                    icon="✨"
                    color="purple"
                />
            </div>

            {/* Graphe de tendance : CA sur 6 mois */}
            <div className="mb-8">
                <RevenueChart data={analytics.revenueByMonth} />
            </div>

            <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
                {/* Sales Chart */}
                <div className="lg:col-span-2">
                    <SalesChart data={analytics.productSales} />
                </div>

                {/* Recent Orders */}
                <div className="rounded-xl bg-white p-6 shadow-lg dashboard-recent-orders">
                    <div className="flex justify-between items-center mb-6">
                        <h3 className="text-lg font-bold text-gray-800 m-0">Commandes Récentes</h3>

                        {/* Pagination Controls */}
                        {analytics.recentOrders.length > ORDERS_PER_PAGE && (
                            <div className="flex gap-2">
                                <button
                                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                    disabled={currentPage === 1}
                                    className="p-1 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed text-lg leading-none"
                                >
                                    ⬆️
                                </button>
                                <span className="text-sm font-medium text-gray-500 self-center min-w-[60px] text-center">
                                    {currentPage} / {Math.ceil(analytics.recentOrders.length / ORDERS_PER_PAGE)}
                                </span>
                                <button
                                    onClick={() => setCurrentPage(p => Math.min(Math.ceil(analytics.recentOrders.length / ORDERS_PER_PAGE), p + 1))}
                                    disabled={currentPage >= Math.ceil(analytics.recentOrders.length / ORDERS_PER_PAGE)}
                                    className="p-1 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed text-lg leading-none"
                                >
                                    ⬇️
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Alerte en tête de liste : une commande payée dont l'étiquette
                        n'a pas pu être créée demande une action manuelle, et rien
                        d'autre ne prévient l'owner. Elle n'apparaît que s'il y en a. */}
                    {analytics.recentOrders.some(o => demandeUneAction(o.status)) && (
                        <div className="mb-4 rounded-lg border-2 border-red-300 bg-red-50 p-3">
                            <p className="m-0 text-sm font-bold text-red-800">
                                ⚠️ {analytics.recentOrders.filter(o => demandeUneAction(o.status)).length} commande(s)
                                payée(s) sans étiquette d'expédition
                            </p>
                            <p className="m-0 mt-1 text-xs text-red-700">
                                Sendcloud n'a pas pu créer l'étiquette. Le client a payé et attend
                                son colis : créez-la à la main dans Sendcloud.
                            </p>
                        </div>
                    )}

                    <div className="flex flex-col gap-4 min-h-[300px]">
                        {analytics.recentOrders.length > 0 ? (
                            analytics.recentOrders
                                .slice((currentPage - 1) * ORDERS_PER_PAGE, currentPage * ORDERS_PER_PAGE)
                                .map(order => (
                                    <div
                                        key={order.id}
                                        onClick={() => setSelectedOrder(order)}
                                        className="flex items-center justify-between border-b border-gray-100 pb-4 last:border-0 last:pb-0 cursor-pointer hover:bg-gray-50 p-2 rounded transition-colors animate-fade-in"
                                    >
                                        <div className="flex flex-col">
                                            <span className="font-semibold text-gray-900">#{order.orderNumber}</span>
                                            <span className="text-sm text-gray-500">{order.user?.fullName || 'Client Inconnu'}</span>
                                        </div>
                                        <div className="flex flex-col items-end gap-1">
                                            <span className="font-bold text-gray-900">{Number(order.totalPrice).toFixed(2)}€</span>
                                            <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${classeStatut(order.status)}`}>
                                                {libelleStatut(order.status)}
                                            </span>
                                        </div>
                                    </div>
                                ))
                        ) : (
                            <div className="flex items-center justify-center h-full text-gray-500">
                                Aucune commande récente
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Modal */}
            {selectedOrder && (
                <OrderDetailsModal
                    order={selectedOrder}
                    onClose={() => setSelectedOrder(null)}
                />
            )}

            {showUsersModal && analytics.usersList && (
                <UsersListModal
                    users={analytics.usersList}
                    onClose={() => setShowUsersModal(false)}
                />
            )}
        </div>
    );
};

export default DashboardHome;
